/** Pure stock planning. Quantities in recipes are grams; inventory keeps its declared unit. */
const EPSILON = 0.000001;
const number = (value, label, { positive = false, integer = false } = {}) => {
  const parsed = Number(value);
  if (value === '' || value === null || value === undefined || !Number.isFinite(parsed) || parsed < 0 || (positive && parsed <= 0) || (integer && !Number.isInteger(parsed))) {
    throw new Error(`${label} must be ${positive ? 'a positive' : 'a non-negative'} ${integer ? 'whole number' : 'number'}.`);
  }
  return parsed;
};
export const quantity = number;
export const normalizeUnit = (unit = 'g') => {
  const normalized = String(unit).trim().toLowerCase();
  return ({ grams: 'g', gram: 'g', kilograms: 'kg', kilogram: 'kg', liters: 'l', litres: 'l', liter: 'l', litre: 'l', milliliters: 'ml', pieces: 'pcs', piece: 'pcs', pc: 'pcs', unit: 'pcs', units: 'pcs' })[normalized] || normalized;
};
const MASS = { g: 1, kg: 1000, oz: 28.349523125, lb: 453.59237 };
const VOLUME = { ml: 1, l: 1000 };
export const convertQuantity = (amount, from, to, density) => {
  const value = number(amount, 'Quantity');
  const source = normalizeUnit(from);
  const target = normalizeUnit(to);
  if (source === target) return value;
  if (MASS[source] && MASS[target]) return value * MASS[source] / MASS[target];
  if (VOLUME[source] && VOLUME[target]) return value * VOLUME[source] / VOLUME[target];
  const rho = Number(density);
  if (rho > 0 && Number.isFinite(rho)) {
    if (MASS[source] && VOLUME[target]) return value * MASS[source] / rho / VOLUME[target];
    if (VOLUME[source] && MASS[target]) return value * VOLUME[source] * rho / MASS[target];
  }
  throw new Error(`Cannot convert ${source} to ${target}${(MASS[source] && VOLUME[target]) || (VOLUME[source] && MASS[target]) ? ' without a density in g/ml' : ''}. Update the inventory unit or link a compatible item.`);
};
export const inventoryUnit = item => item.stock !== undefined && item.stock !== null ? normalizeUnit(item.unit || 'g') : item.stockGrams !== undefined ? 'g' : normalizeUnit(item.unit || 'g');
export const inventoryStock = item => number(item.stock ?? item.stockGrams ?? 0, `${item.name || 'Item'} stock`);
export const inventoryDensity = item => item.densityGPerMl ?? item.density;
export const inventoryUnitCost = item => {
  const declaredUnit = normalizeUnit(item.unit || inventoryUnit(item));
  const unit = inventoryUnit(item);
  return number(item.costPerUnit ?? item.cost ?? 0, 'Unit cost') / convertQuantity(1, declaredUnit, unit, inventoryDensity(item));
};
export const stockPatch = (item, nextStock) => {
  const value = number(nextStock < 0 && nextStock > -EPSILON ? 0 : nextStock, `${item.name || 'Item'} stock`);
  const unit = inventoryUnit(item);
  const result = { stock: value, unit };
  if (MASS[unit]) result.stockGrams = convertQuantity(value, unit, 'g');
  return result;
};
export const resolveInventoryItem = (reference, items, label = 'Ingredient') => {
  const ref = typeof reference === 'string' ? { id: reference } : reference || {};
  const ids = [ref.firebaseId, ref.ingredientId, ref.id].filter(Boolean);
  const direct = items.filter(item => ids.includes(item.id) || ids.includes(item.firebaseId));
  if (direct.length === 1) return direct[0];
  if (direct.length > 1) throw new Error(`${label} has ambiguous inventory links. Link it to one inventory item.`);
  const key = String(ref.key || '').trim().toLowerCase();
  const name = String(ref.name || '').trim().toLowerCase();
  const matches = items.filter(item => (key && String(item.key || '').toLowerCase() === key) || (name && String(item.name || '').trim().toLowerCase() === name));
  if (matches.length === 1) return matches[0];
  throw new Error(`${label} ${ref.name || ref.key || ids[0] || ''} ${matches.length ? 'matches multiple inventory items' : 'is not linked to inventory'}. Add the ingredient to Inventory and select the matching item in the recipe.`);
};
export const planPurchase = (ingredient, purchase) => {
  const bought = number(purchase.quantity, 'Purchase quantity', { positive: true });
  const totalCost = number(purchase.totalCost, 'Total cost');
  const stock = inventoryStock(ingredient);
  const unit = inventoryUnit(ingredient);
  const added = convertQuantity(bought, purchase.unit, unit, inventoryDensity(ingredient));
  const currency = ingredient.currencyCode || purchase.currencyCode || 'INR';
  if (purchase.currencyCode && purchase.currencyCode !== currency) throw new Error(`This ingredient is costed in ${currency}. Record the purchase in the same currency before updating stock.`);
  const oldUnitCost = inventoryUnitCost(ingredient);
  const nextStock = stock + added;
  return { patch: { ...stockPatch(ingredient, nextStock), costPerUnit: (stock * oldUnitCost + totalCost) / nextStock, currencyCode: currency, linkedRecordCount: Number(ingredient.linkedRecordCount || 0) + 1 }, movement: { collection: 'ingredients', id: ingredient.id, name: ingredient.name, quantity: added, unit, totalCost, currencyCode: currency } };
};
export const planPurchaseReversal = (ingredient, movement) => {
  const unit = inventoryUnit(ingredient);
  const removed = convertQuantity(movement.quantity, movement.unit, unit, inventoryDensity(ingredient));
  const stock = inventoryStock(ingredient);
  if (stock + EPSILON < removed) throw new Error('This purchase cannot be removed because some of its stock has been consumed. Correct the linked production records first.');
  const nextStock = Math.max(0, stock - removed);
  const remainingValue = stock * inventoryUnitCost(ingredient) - Number(movement.totalCost || 0);
  if (remainingValue < -0.01 || (nextStock < EPSILON && Math.abs(remainingValue) > 0.01)) throw new Error('This purchase cannot be safely reversed after stock or cost adjustments. Reconcile the ingredient valuation first.');
  return { ...stockPatch(ingredient, nextStock), costPerUnit: nextStock > EPSILON ? Math.max(0, remainingValue) / nextStock : 0, linkedRecordCount: Math.max(0, Number(ingredient.linkedRecordCount || 0) - 1) };
};
export const planStockMovements = (requirements, records, reverse = false) => {
  const combined = new Map();
  requirements.forEach(requirement => {
    const collection = requirement.collection || 'ingredients';
    const items = records[collection] || [];
    const item = resolveInventoryItem(requirement, items, collection === 'packaging' ? 'Packaging' : 'Ingredient');
    const unit = inventoryUnit(item);
    const required = convertQuantity(requirement.quantity, requirement.unit || 'g', unit, inventoryDensity(item));
    const key = `${collection}/${item.id}`;
    const previous = combined.get(key);
    if (reverse && requirement.currencyCode && requirement.currencyCode !== (item.currencyCode || 'INR')) throw new Error(`${item.name || 'Item'} currency changed after production. Restore its original currency before reversing this batch.`);
    const historicalCost = reverse ? number(requirement.totalCost ?? required * inventoryUnitCost(item), 'Recorded movement cost') : 0;
    combined.set(key, { historicalCost: (previous?.historicalCost || 0) + historicalCost, collection, id: item.id, name: item.name || requirement.name || '', category: item.category || item.type || collection, quantity: (previous?.quantity || 0) + required, unit, item });
  });
  const movements = [];
  const updates = [];
  combined.forEach(({ item, historicalCost, ...movement }) => {
    const stock = inventoryStock(item);
    if (!reverse && stock + EPSILON < movement.quantity) throw new Error(`Insufficient ${item.name || movement.id}: need ${movement.quantity.toFixed(3)} ${movement.unit}, available ${stock.toFixed(3)} ${movement.unit}.`);
    const costPerUnit = inventoryUnitCost(item);
    const next = reverse ? stock + movement.quantity : Math.max(0, stock - movement.quantity);
    let grams = null;
    try { grams = convertQuantity(movement.quantity, movement.unit, 'g', inventoryDensity(item)); } catch { /* Packaging pieces have no mass. */ }
    movements.push({ ...movement, grams, costPerUnit, totalCost: movement.quantity * costPerUnit, currencyCode: item.currencyCode || 'INR' });
    const patch = { ...stockPatch(item, next), costPerUnit, linkedRecordCount: Math.max(0, Number(item.linkedRecordCount || 0) + (reverse ? -1 : 1)) };
    if (reverse && next > EPSILON) patch.costPerUnit = (stock * costPerUnit + historicalCost) / next;
    updates.push({ collection: movement.collection, id: item.id, patch });
  });
  return { movements, updates };
};
export const planFinishedSale = (batch, sale, reverse = false) => {
  const count = number(sale.quantity, 'Sale quantity', { positive: true, integer: true });
  if (!Number.isFinite(Number(batch.remainingBars)) || batch.remainingBars === null || batch.remainingBars === undefined) throw new Error('This legacy batch has no verified remaining stock. Reconcile its stock before linking a sale, or record this sale without a batch.');
  const remaining = number(batch.remainingBars, 'Remaining bars', { integer: true });
  const produced = number(batch.bars, 'Produced bars', { positive: true, integer: true });
  if (!reverse && !['ready', 'sold'].includes(batch.status)) throw new Error('Only a ready batch can be sold. Complete curing and quality checks first.');
  if (!reverse && count > remaining) throw new Error(`Insufficient finished stock: ${remaining} bars remain in this batch.`);
  if (reverse && remaining + count > produced) throw new Error('This sale cannot be reversed because the batch stock was adjusted. Reconcile the batch first.');
  const next = reverse ? remaining + count : remaining - count;
  return { remainingBars: next, soldBars: produced - next, status: next === 0 ? 'sold' : 'ready' };
};
export const getBatchCureDays = (batch, recipe) => {
  const value = Number(batch?.cureDays ?? batch?.recipeSnapshot?.cureDays ?? recipe?.cureDays ?? recipe?.cureTimeDays ?? 42);
  return Number.isFinite(value) && value >= 0 ? value : 42;
};
export const getBatchReadyDate = (batch, recipe) => {
  if (batch.readyDate) {
    const ready = new Date(`${String(batch.readyDate).slice(0, 10)}T12:00:00`);
    return Number.isNaN(ready.getTime()) ? null : ready;
  }
  if (!batch.madeDate) return null;
  const date = new Date(`${String(batch.madeDate).slice(0, 10)}T12:00:00`);
  date.setDate(date.getDate() + getBatchCureDays(batch, recipe));
  return Number.isNaN(date.getTime()) ? null : date;
};
export const aggregateProductionUsage = (batches, ingredients = [], convertCost = value => value) => {
  const usage = new Map();
  ingredients.forEach(item => usage.set(item.id, { id: item.id, name: item.name || item.key, category: item.category || item.type || 'unknown', recipeCount: 0, totalGrams: 0, avgPercentage: 0, recipes: [], totalCost: 0, currentStock: (() => { try { return convertQuantity(inventoryStock(item), inventoryUnit(item), 'g', inventoryDensity(item)); } catch { return 0; } })() }));
  batches.filter(batch => batch.inventoryApplied === true && Array.isArray(batch.stockMovements)).forEach(batch => {
    batch.stockMovements.filter(m => m.collection === 'ingredients').forEach(movement => {
      const row = usage.get(movement.id) || { id: movement.id, name: movement.name, category: movement.category || 'unknown', recipeCount: 0, totalGrams: 0, avgPercentage: 0, recipes: [], totalCost: 0, currentStock: 0 };
      row.recipeCount += 1;
      row.totalGrams += Number(movement.grams) || 0;
      row.totalCost += convertCost(Number(movement.totalCost) || 0, movement.currencyCode || batch.currencyCode || 'INR');
      row.recipes.push(batch.name || batch.recipeName || 'Batch');
      usage.set(movement.id, row);
    });
  });
  return [...usage.values()].sort((a, b) => b.totalGrams - a.totalGrams);
};
